<script setup lang="ts">
import { ref, nextTick } from 'vue'

const props = defineProps<{
  streaming: boolean
  thinking: boolean
  nodeStatus: { node: string | null; detail: string | null } | null
}>()
const emit = defineEmits<{
  send: [text: string]
  abort: []
}>()

const text = ref('')
const textarea = ref<HTMLTextAreaElement | null>(null)

function autoResize() {
  const el = textarea.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 200) + 'px'
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    send()
  }
}

function send() {
  const msg = text.value.trim()
  if (!msg || props.streaming) return
  emit('send', msg)
  text.value = ''
  nextTick(autoResize)
}
</script>

<template>
  <div class="px-5 py-3 border-t border-neutral-200 bg-white shrink-0">
    <div v-if="streaming" class="flex items-center gap-1.5 text-xs text-neutral-500 pb-1.5">
      <span
        class="inline-block w-1.5 h-1.5 rounded-full animate-pulse-dot"
        :class="[
          nodeStatus?.node ? 'bg-indigo-500' :
          thinking ? 'bg-violet-500' : 'bg-blue-500'
        ]"
      />
      {{
        nodeStatus?.node
          ? (nodeStatus.detail || `${nodeStatus.node} running…`)
          : thinking
            ? 'Thinking…'
            : 'Responding…'
      }}
    </div>

    <div class="flex gap-2 items-end">
      <textarea
        ref="textarea"
        v-model="text"
        class="flex-1 px-3.5 py-2.5 bg-[#fafafa] border border-neutral-300 rounded-[10px] text-neutral-800 text-sm resize-none min-h-[44px] max-h-[200px] leading-normal focus:outline-none focus:border-blue-500 disabled:opacity-50 placeholder-neutral-400"
        :disabled="streaming"
        placeholder="Ask FORESIGHT Smart Assist…"
        rows="1"
        @keydown="onKeydown"
        @input="autoResize"
      />
      <button
        v-if="streaming"
        class="px-4 py-2.5 bg-red-600 hover:bg-red-500 text-white rounded-[10px] text-sm whitespace-nowrap transition-colors"
        @click="$emit('abort')"
      >
        Stop
      </button>
      <button
        v-else
        class="px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-[10px] text-sm whitespace-nowrap disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors"
        :disabled="!text.trim()"
        @click="send"
      >
        Send
      </button>
    </div>
  </div>
</template>
